import React from "react"
import { t } from "i18next"
import Recaptcha from "react-grecaptcha"
import { IOWidget, Updater, any, async, fromJSX, loadedAsyncState } from "widgets-for-react"
import { FormControls } from "../../../../../../../Shared/Frontend/forms"
import { capitalize } from "../../../../../../../Shared/Frontend/utils/capitalize"
import { Loader } from "../../../../../../../Shared/Frontend/utils/loader"
import { singleValidatorResultBuilders } from "../../../../../../../Shared/Frontend/validators/validatorExpr"
import { possibleSalutations } from "../../donationFormBlock/donationFormBlock.state"
import { isFieldRequired } from "../productFormBlock.api"
import { FormInput, ProductFormState, getValidation, possibleSalutationsType } from "../productFormBlock.state"
import { formInfoUpdaters, formSegmentUpdaters, productFormUpdaters } from "../productFormBlock.updaters"
import { formSegmentWidget } from "./segment"

type fieldUpdater<T> = (_: T) => void

type Updaters = {
  emailUpdater: fieldUpdater<string>,
  salutationsUpdater: fieldUpdater<possibleSalutationsType>
  firstNameUpdater: fieldUpdater<string>,
  insertionUpdater: fieldUpdater<string>,
  lastNameUpdater: fieldUpdater<string>,
  postalCodeUpdater: fieldUpdater<string>,
  houseNumberUpdater: fieldUpdater<string>,
  houseNumberAdditionUpdater: fieldUpdater<string>,
  phoneUpdater: fieldUpdater<string>,
  acceptNewsLetterUpdater: fieldUpdater<boolean>,
  onBlur: (field: keyof FormInput) => () => void,
  recaptchaUpdater: fieldUpdater<string>,
  onSubmit: (s: ProductFormState) => void,
}

const FormLayout = (props: {
  updaters: Updaters
  state: ProductFormState
  formSegment: React.ReactNode
}) => {
  const { updaters, state } = props

  if (state.donationFormLoader.kind !== "loaded") return <Loader type="inline" />
  const form = state.donationFormLoader.value

  const genderValidation = getValidation(state, "salutation")
  const firstnameValidation = getValidation(state, "firstName")
  const infixValidation = getValidation(state, "insertion")
  const lastnameValidation = getValidation(state, "lastName")
  const postalcodeValidation = getValidation(state, "postalCode")
  const houseNumberValidation = getValidation(state, "houseNumber")
  const houseNumberAdditionValidation = getValidation(state, "houseNumberAddition")
  const streetValidation = singleValidatorResultBuilders.failed("street", t("DonationBlock:DonationForm.Error_street"))
  const cityValidation = singleValidatorResultBuilders.failed("street", t("DonationBlock:DonationForm.Error_city"))
  const emailValidation = singleValidatorResultBuilders.failed("email", t("DonationBlock:DonationForm.Error_email_empty"))
  const email2ndValidation = getValidation(state, "email")
  const phoneValidation = getValidation(state, "phone")

  return <>
    <FormControls.FormRadioInGroup
      key={`donation-gender-b${state.blockIndex}`}
      name={`donation-gender-b${state.blockIndex}`}
      legend={t("DonationBlock:DonationForm.Label_salutation")}
      disabled={false}
      required={false}
      errorMessage={genderValidation.kind == 'failed' ? genderValidation.message : undefined}
      items={possibleSalutations.map((item, index) => ({
        controlId: `donation-gender--b${state.blockIndex}-${index}`,
        label: capitalize(item.replace(/_/g, ' ')),
        checked: item == state.input.salutation ? true : false,
        onChange: (checked) => checked ? updaters.salutationsUpdater(item) : () => { }
      }))}
    />

    <div className='donation__row row--30-30-40'>
      <FormControls.FormItemInGroup
        key={`donation-firstname-b${state.blockIndex}`}
        controlId={`donation-firstname-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_firstName")}
        disabled={false}
        required={isFieldRequired('firstName')}
        value={state.input.firstName}
        placeholder=''
        onChange={updaters.firstNameUpdater}
        onBlur={updaters.onBlur('firstName')}
        errorMessage={firstnameValidation.kind == 'failed' ? firstnameValidation.message : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-infix-b${state.blockIndex}`}
        controlId={`donation-infix-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={<><span className="label--small">{t("DonationBlock:DonationForm.Label_insertion_s")}</span><span className="label--large">{t("DonationBlock:DonationForm.Label_insertion")}</span></>}
        disabled={false}
        required={isFieldRequired('insertion')}
        value={state.input.insertion}
        placeholder=''
        onChange={updaters.insertionUpdater}
        onBlur={updaters.onBlur('insertion')}
        errorMessage={infixValidation.kind == 'failed' ? infixValidation.message : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-lastname-b${state.blockIndex}`}
        controlId={`donation-lastname-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_lastName")}
        disabled={false}
        required={isFieldRequired('lastName')}
        value={state.input.lastName}
        placeholder=''
        onChange={updaters.lastNameUpdater}
        onBlur={updaters.onBlur('lastName')}
        errorMessage={lastnameValidation.kind == 'failed' ? lastnameValidation.message : undefined}
      />
    </div>

    <div className='donation__row row--33'>
      <FormControls.FormItemInGroup
        key={`donation-postalcode-b${state.blockIndex}`}
        controlId={`donation-postalcode-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_postalCode")}
        disabled={false}
        required={isFieldRequired('postalCode')}
        value={state.input.postalCode}
        onBlur={updaters.onBlur('postalCode')}
        placeholder=''
        onChange={updaters.postalCodeUpdater}
        errorMessage={postalcodeValidation.kind == 'failed' ? postalcodeValidation.message : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-housenumber-b${state.blockIndex}`}
        controlId={`donation-housenumber-b${state.blockIndex}`}
        type='text'
        inputMode='numeric'
        label={<><span className="label--small">{t("DonationBlock:DonationForm.Label_houseNumber_s")}</span><span className="label--large">{t("DonationBlock:DonationForm.Label_houseNumber")}</span></>}
        disabled={false}
        required={isFieldRequired('houseNumber')}
        value={state.input.houseNumber}
        placeholder=''
        onChange={updaters.houseNumberUpdater}
        onBlur={updaters.onBlur('houseNumber')}
        errorMessage={houseNumberValidation.kind == 'failed' ? houseNumberValidation.message : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-addition-b${state.blockIndex}`}
        controlId={`donation-addition-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={<><span className="label--small">{t("DonationBlock:DonationForm.Label_houseNumberAddition_s")}</span><span className="label--large">{t("DonationBlock:DonationForm.Label_houseNumberAddition")}</span></>}
        disabled={false}
        required={isFieldRequired('houseNumberAddition')}
        value={state.input.houseNumberAddition}
        placeholder=''
        onChange={updaters.houseNumberAdditionUpdater}
        onBlur={updaters.onBlur('houseNumberAddition')}
        errorMessage={houseNumberAdditionValidation.kind == 'failed' ? houseNumberAdditionValidation.message : undefined}
      />
    </div>

    <div className='donation__row row--50-50'>
      <FormControls.FormItemInGroup
        key={`donation-street-b${state.blockIndex}`}
        controlId={`donation-street-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_street")}
        disabled={true}
        required={isFieldRequired('street')}
        value={state.input.street}
        placeholder=''
        onChange={() => { }}
        errorMessage={state.input.invalidHousenumberPostcodeCombination && streetValidation.kind == 'failed' ? streetValidation.message : undefined}
      />

      <FormControls.FormItemInGroup
        key={`donation-city-b${state.blockIndex}`}
        controlId={`donation-city-b${state.blockIndex}`}
        type='text'
        inputMode='text'
        label={t("DonationBlock:DonationForm.Label_city")}
        disabled={true}
        required={isFieldRequired('city')}
        value={state.input.city}
        placeholder=''
        onChange={() => { }}
        errorMessage={state.input.invalidHousenumberPostcodeCombination && cityValidation.kind == 'failed' ? cityValidation.message : undefined}
      />
    </div>

    <FormControls.FormItemInGroup
      key={`donation-tel-b${state.blockIndex}`}
      controlId={`donation-tel-b${state.blockIndex}`}
      type='tel'
      inputMode='tel'
      label={t("DonationBlock:DonationForm.Label_phone")}
      disabled={false}
      required={isFieldRequired('phone')}
      value={state.input.phone}
      placeholder=''
      onChange={updaters.phoneUpdater}
      onBlur={updaters.onBlur('phone')}
      errorMessage={phoneValidation.kind == 'failed' ? phoneValidation.message : undefined}
    />

    <FormControls.FormItemInGroup
      key={`donation-email-b${state.blockIndex}`}
      controlId={`donation-email-b${state.blockIndex}`}
      type='email'
      inputMode='email'
      label={t("DonationBlock:DonationForm.Label_email")}
      disabled={false}
      required={isFieldRequired('email')}
      value={state.input.email}
      placeholder=''
      onChange={updaters.emailUpdater}
      onBlur={updaters.onBlur('email')}
      errorMessage={state.validatorInput?.email !== undefined && state.validatorResult.path('email').kind == 'failed' && state.input.email == ''
        ? emailValidation.kind == 'failed' ? emailValidation.message : undefined
        : email2ndValidation.kind == 'failed' ? email2ndValidation.message : undefined}
    />

    <FormControls.FormCheckInGroup
      key={`donation-newsletter-b${state.blockIndex}`}
      name={`donation-newsletter-b${state.blockIndex}`}
      legend={undefined}
      disabled={false}
      required={true}
      items={[t("DonationBlock:DonationForm.Label_acceptNewsletter")].map((item, index) => ({
        controlId: `donation-newsletter-b${state.blockIndex}-${index}`,
        label: capitalize(item),
        checked: state.input.acceptNewsLetter,
        onChange: updaters.acceptNewsLetterUpdater
      }))}
    />
    <div id="recaptcha-content">
      <Recaptcha
        id="dtf-recaptcha"
        locale="NL_nl"
        sitekey={(document.getElementById('recaptchaToken') as any).value}
        callback={
          (r: string) => {
            updaters.recaptchaUpdater(r)
            updaters.onSubmit(state)
          }
        }
        expiredCallback={() => window.grecaptcha.reset()}
        invisible
      />
    </div>

    {props.formSegment}

    <div className='donation__action'>
      <button
        className='button button--primary'
        onClick={() => {
          window.grecaptcha.reset()
          window.grecaptcha.execute()
        }}
        disabled={state.submitState.kind == 'loading'}
      >
        <span className="button__label">{form.submitButton || t("ProductForm:Form.Submit")}</span>
      </button>
    </div>

    <div className='donation__avg text--styled text--s'>
      <p dangerouslySetInnerHTML={{ __html: t("DonationBlock:DonationForm.avg") }} />
    </div>
  </>
}

export const formInputWidget: IOWidget<ProductFormState, Updater<ProductFormState>> = s =>
  any([
    s.validator.widget().map((v) => productFormUpdaters.updateValidator(v)),
    s.productValidator.widget().map((v) => formSegmentUpdaters.updateProductValidator(v)),
    async(s.zipCodeLoader).map(productFormUpdaters.zipCodeLoaderUpdater),
    fromJSX(updater => {
      if (s.donationFormLoader.kind !== "loaded") return <Loader type="inline" />
      const form = s.donationFormLoader.value
      return <FormLayout
        updaters={formInfoUpdaters(updater)}
        state={s}
        formSegment={
          formSegmentWidget(form, s)
            .map<Updater<ProductFormState>>(u => s0 => {
              if (s0.donationFormLoader.kind !== "loaded") return s0
              const newForm = u(s0.donationFormLoader.value)

              const s1 = { ...s0, donationFormLoader: loadedAsyncState(newForm) }
              if (newForm.productForm.kind === "product_sale_donation" || newForm.productForm.kind === "product_package_costs") {
                return formSegmentUpdaters.validateFields()(s1)
              }

              return s1
            })
            .run(updater)
        }
      />
    })
  ])
